import React, { useState, useEffect, useRef } from "react";
import styles from "../styles/assistantSetup.module.css";

const AssistantSetup = ({ startAutomation, isLaunchDisabled, launchState }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [status, setStatus] = useState({ message: "", type: "" });
  const [isDragging, setIsDragging] = useState(false);

  const [resumeData, setResumeData] = useState({
    base64: null,
    filename: null,
    text: "",
  });

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [portfolio, setPortfolio] = useState("");

  const [willingToRelocate, setWillingToRelocate] = useState(false);

  const [jobRole, setJobRole] = useState("");
  const [location, setLocation] = useState("");

  const resumeFileInputRef = useRef(null);

  useEffect(() => {
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js`;
    }
    try {
      const savedWellfoundSettings = localStorage.getItem("wellfoundSettings");
      const savedResumeData = localStorage.getItem("resumeData");

      if (savedResumeData) setResumeData(JSON.parse(savedResumeData));

      if (savedWellfoundSettings) {
        const settings = JSON.parse(savedWellfoundSettings);
        setFullName(settings.fullName || "");
        setEmail(settings.email || "");
        setPhone(settings.phone || "");
        setLinkedin(settings.linkedin || "");
        setPortfolio(settings.portfolio || "");
        setWillingToRelocate(settings.willingToRelocate || false);
        setJobRole(settings.jobRole || "");
        setLocation(settings.location || "");
      }
    } catch (error) {
      console.error("Failed to load settings", error);
    }
  }, []);

  const showStatus = (message, type) => {
    setStatus({ message, type });
    setTimeout(() => setStatus({ message: "", type: "" }), 5000);
  };

  const processResumeFile = async (file) => {
    if (!file || file.type !== "application/pdf") {
      showStatus("Invalid file. Please upload a PDF.", "error");
      return;
    }
    showStatus("Processing resume...", "info");

    const toBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });

    const parseText = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsArrayBuffer(file);
        reader.onload = async (e) => {
          try {
            if (!window.pdfjsLib) throw new Error("PDF library not loaded.");
            const pdf = await window.pdfjsLib.getDocument({
              data: e.target.result,
            }).promise;
            const textContent = (
              await Promise.all(
                Array.from({ length: pdf.numPages }, (_, i) =>
                  pdf.getPage(i + 1).then((page) => page.getTextContent())
                )
              )
            )
              .map((content) => content.items.map((item) => item.str).join(" "))
              .join("\n");
            resolve(textContent);
          } catch (error) {
            reject(error);
          }
        };
      });

    try {
      const [base64, text] = await Promise.all([
        toBase64(file),
        parseText(file),
      ]);
      setResumeData({ base64, filename: file.name, text });
      showStatus(`Resume "${file.name}" processed.`, "success");
    } catch (error) {
      showStatus(`Error processing resume: ${error.message}`, "error");
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length) processResumeFile(e.dataTransfer.files[0]);
  };
  const handleFileChange = (e) => {
    if (e.target.files.length) processResumeFile(e.target.files[0]);
  };

  const handleSaveAll = () => {
    setIsSaving(true);
    showStatus("Saving settings...", "info");
    try {
      const wellfoundSettings = {
        fullName,
        email,
        phone,
        linkedin,
        portfolio,
        willingToRelocate,
        jobRole,
        location,
        resumeText: resumeData.text,
      };
      localStorage.setItem(
        "wellfoundSettings",
        JSON.stringify(wellfoundSettings)
      );
      if (resumeData.base64)
        localStorage.setItem("resumeData", JSON.stringify(resumeData));
      setTimeout(() => {
        showStatus("Settings saved successfully!", "success");
        setIsSaving(false);
      }, 500);
    } catch (error) {
      showStatus(`Failed to save: ${error.message}`, "error");
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.assistantSetupContainer}>
      <div className={styles.formCard}>
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <i className="fas fa-user-tie"></i> Wellfound Profile Details
          </h3>

          <div className={styles.profileGrid}>
            <div className={`${styles.formGroup} ${styles.span6}`}>
              <label>Upload Resume (PDF only)</label>
              <div
                className={`${styles.fileDropArea} ${
                  isDragging ? styles.dragging : ""
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => resumeFileInputRef.current.click()}
              >
                <input
                  id="resume-upload"
                  type="file"
                  ref={resumeFileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf"
                  style={{ display: "none" }}
                />
                <div className={styles.fileDropContent}>
                  <i className="fas fa-cloud-upload-alt"></i>
                  <span className={styles.uploadText}>
                    <strong>Choose a file</strong> or drag it here.
                  </span>
                  <span className={styles.filePathDisplay}>
                    {resumeData.filename || "No file selected."}
                  </span>
                </div>
              </div>
            </div>

            <div className={`${styles.formGroup} ${styles.span2}`}>
              <label>Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div className={`${styles.formGroup} ${styles.span2}`}>
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="john@example.com"
              />
            </div>
            <div className={`${styles.formGroup} ${styles.span2}`}>
              <label>Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555-0123"
              />
            </div>

            <div className={`${styles.formGroup} ${styles.span3}`}>
              <label>
                <i
                  className="fab fa-linkedin"
                  style={{
                    color: "#0077b5",
                    marginRight: "6px",
                    fontSize: "1.1em",
                  }}
                ></i>
                LinkedIn URL
              </label>
              <input
                type="url"
                value={linkedin}
                onChange={(e) => setLinkedin(e.target.value)}
                placeholder="https://linkedin.com/in/..."
              />
            </div>
            <div className={`${styles.formGroup} ${styles.span3}`}>
              <label>
                <i
                  className="fas fa-globe"
                  style={{ color: "#555", marginRight: "6px" }}
                ></i>
                Portfolio / Website
              </label>
              <input
                type="url"
                value={portfolio}
                onChange={(e) => setPortfolio(e.target.value)}
                placeholder="https://myportfolio.com"
              />
            </div>

            <div className={`${styles.relocationContainer} ${styles.span6}`}>
              <div className={styles.checkboxWrapper}>
                <input
                  type="checkbox"
                  id="relocateCheck"
                  className={styles.customCheckbox}
                  checked={willingToRelocate}
                  onChange={(e) => setWillingToRelocate(e.target.checked)}
                />
                <div className={styles.checkboxLabelContent}>
                  <label
                    htmlFor="relocateCheck"
                    className={styles.checkboxTitle}
                  >
                    Willing to Relocate?
                  </label>
                  {willingToRelocate && (
                    <span className={styles.checkboxSubtitle}>
                      <i className="fas fa-check-circle"></i> Automation will
                      auto-select relocation options if required.
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className={styles.divider} />

        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <i className="fas fa-search"></i> Job Search Configuration
          </h3>
          <div className={`${styles.formGrid} ${styles.filtersGrid}`}>
            <div className={styles.formGroup}>
              <label>Job Role</label>
              <input
                type="text"
                value={jobRole}
                onChange={(e) => setJobRole(e.target.value)}
                placeholder="e.g., Software Engineer"
              />
            </div>
            <div className={styles.formGroup}>
              <label>Location</label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g., Remote"
              />
            </div>
          </div>
        </div>

        <div className={styles.actionsContainer}>
          {status.message && (
            <div className={`${styles.statusMessage} ${styles[status.type]}`}>
              {status.message}
            </div>
          )}
          <button
            className={styles.primaryButton}
            onClick={handleSaveAll}
            disabled={isSaving}
          >
            <i
              className={`fas ${isSaving ? "fa-spinner fa-spin" : "fa-save"}`}
            ></i>{" "}
            {isSaving ? "Saving..." : "Save Configuration"}
          </button>
        </div>
      </div>

      <button
        className={styles.startAutomationBtn}
        onClick={startAutomation}
        disabled={isLaunchDisabled}
        title={launchState?.text}
      >
        <i className="fas fa-play-circle"></i>{" "}
        <span className={styles.buttonText}>
          {launchState?.text || "Start Wellfound Automation"}
        </span>
      </button>
    </div>
  );
};

export default AssistantSetup;
